import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useSyncExternalStore } from 'react';
import { AnyActorRef } from 'xstate';
import { SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS } from './constants';
import { NavEntry } from './NavEntry';

// Shared store for which subject groups the user collapsed. A module-level store
// (read via useSyncExternalStore) is the single source of truth so that:
//  - the preference survives the sidebar being hidden/re-opened (which unmounts
//    this component) and full page reloads — backed by localStorage (MCV-1497);
//  - every NavComponent mounted in the same document stays in sync live, rather
//    than each holding an independent copy that only re-reads storage on mount —
//    a same-document custom event notifies all subscribers on every change;
//  - changes in other tabs are also picked up, via the native `storage` event.
// All localStorage access is guarded for private-mode / storage-unavailable envs.
const COLLAPSED_SECTIONS_STORAGE_KEY = 'vault:sidebar:collapsedSections';
const COLLAPSED_SECTIONS_EVENT = 'vault:sidebar:collapsedSectionsChange';

const readCollapsedSections = (): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_SECTIONS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

// Cached snapshot so useSyncExternalStore receives a referentially-stable value
// between changes (a fresh object every read would loop it). Reassigned only when
// the preference actually changes (local toggle or cross-tab storage event).
let collapsedSectionsSnapshot: Record<string, boolean> = readCollapsedSections();

const getCollapsedSectionsSnapshot = (): Record<string, boolean> => collapsedSectionsSnapshot;

const setCollapsedSections = (value: Record<string, boolean>) => {
  collapsedSectionsSnapshot = value;
  try {
    window.localStorage.setItem(COLLAPSED_SECTIONS_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore write failures (private mode / quota) — collapse still works in-session.
  }
  // Notify other NavComponent instances mounted in this same document/tab; the
  // native `storage` event only fires in *other* tabs, never the one that wrote.
  window.dispatchEvent(new Event(COLLAPSED_SECTIONS_EVENT));
};

const subscribeCollapsedSections = (onChange: () => void): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== COLLAPSED_SECTIONS_STORAGE_KEY) return;
    collapsedSectionsSnapshot = readCollapsedSections();
    onChange();
  };
  window.addEventListener(COLLAPSED_SECTIONS_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(COLLAPSED_SECTIONS_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
};

export const NavComponent = ({
  keyPrefix,
  actorRef,
  sections = SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS,
}: {
  keyPrefix: string;
  actorRef: AnyActorRef;
  sections?: typeof SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS;
}) => {
  const collapsedSections = useSyncExternalStore(
    subscribeCollapsedSections,
    getCollapsedSectionsSnapshot,
    getCollapsedSectionsSnapshot
  );

  const toggleSection = (label: string) => {
    setCollapsedSections({ ...collapsedSections, [label]: !collapsedSections[label] });
  };

  // The sidebar sends before it navigates, so it passes the tab hash its link is about to put in the
  // URL: a page that loads per tab (academics) would otherwise load whichever tab was last on screen.
  const onSetPage = (page: string, hash?: string) => {
    actorRef.send({ type: 'ON_SET_PAGE', payload: { page, hash } });
  };

  return (
    <>
      {sections.map((section) => {
        const isCollapsed = collapsedSections[section.label];
        return (
          <div
            key={`${keyPrefix}-${section.label}`}
            className="flex flex-col pb-2 border-b border-primary-t-700 mt-1"
          >
            <button
              type="button"
              onClick={() => toggleSection(section.label)}
              aria-expanded={!isCollapsed}
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${section.label}`}
              className="flex w-full items-center justify-between px-4 py-2 text-left transition-colors hover:bg-white/20 hover:cursor-pointer"
            >
              <span
                className="text-sm font-bold uppercase tracking-wider text-primary-t-200"
                data-cy={section.cyTag}
              >
                {section.label}
              </span>
              <ChevronDownIcon
                aria-hidden="true"
                className={`size-4 text-grey-400 transition-transform duration-200 ${
                  isCollapsed ? '-rotate-90' : ''
                }`}
              />
            </button>
            {!isCollapsed &&
              section.subMenu.map((route, idx) => (
                <NavEntry
                  key={`${keyPrefix}-${route.pageKey}-${idx}`}
                  icon={route.icon}
                  label={route.label}
                  href={route.href}
                  pageKey={route.pageKey}
                  activeTab={route.activeTab}
                  isDefaultTab={route.isDefaultTab}
                  onSetPage={onSetPage}
                />
              ))}
          </div>
        );
      })}
    </>
  );
};
